<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useClassStore } from "@/Store/className";
import { classNames } from "@/services/apiServices";
import { useTheme } from "@/composables/useThemes";
const { isDarkTheme } = useTheme();

const classStore = useClassStore();

const initialLoading = ref(false);
const error = ref("");

const classInfoList = computed(() => classStore.classInfoList);
const loading = computed(() => classStore.loading);

const fetchClassInfoList = async () => {
  try {
    initialLoading.value = true;
    const data = await classNames();
    classStore.setClassInfoList(data);
  } catch (err) {
    console.error("Failed to fetch class info list:", err);
    error.value = err.response?.data?.message || "Something went wrong";
  } finally {
    initialLoading.value = false;
  }
};

const onInput = (event) => {
  const value = event.target.value;
  classStore.query = value;
  localStorage.setItem("classSearchQuery", value);

  if (value.trim() === "") {
    fetchClassInfoList();
  } else {
    classStore.SearchClassInfoList(value);
  }
};

onMounted(() => {
  const savedQuery = localStorage.getItem("classSearchQuery") || "";
  if (savedQuery) {
    classStore.query = savedQuery;
    classStore.SearchClassInfoList(savedQuery);
  } else {
    fetchClassInfoList();
  }
});
</script>
<template>
  <v-container class="class-dashboard">
    <v-sheet class="position-relative" style="max-width: 300px">
      <form class="position-relative" @submit.prevent>
        <input
          :value="classStore.query"
          @input="onInput"
          placeholder="Search class name..."
        />
        <button
          type="submit"
          class="src-btn position-absolute top-50 end-0 translate-middle-y bg-transparent p-0 border-0"
        >
          <span class="material-symbols-outlined">search</span>
        </button>
      </form>
    </v-sheet>

    <v-row justify="center" v-if="loading || initialLoading">
      <v-progress-circular indeterminate color="primary" size="40" />
    </v-row>

    <div v-if="error" class="text-error text-center mt-4">
      {{ error }}
    </div>

    <div
      v-else-if="!classInfoList?.length && !loading && !initialLoading"
      class="empty-state text-center"
    >
      <v-icon size="60" color="grey-lighten-1">mdi-account-group</v-icon>
      <h3 class="text-h6 mt-4">No classes found</h3>
      <p class="text-caption">Upload student data to get started</p>
      <v-btn color="primary" to="/uploadFile/student" class="mt-4" size="small">
        <v-icon start>mdi-upload</v-icon>
        Upload Student List
      </v-btn>
    </div>

    <div v-else class="class-list">
      <div
        v-for="(classItem, index) in classInfoList"
        :key="index"
        class="class-item"
        :style="{ background: isDarkTheme ? '#ECFAE5' : '#B5FCCD' }"
        :class="{ verified: classItem.is_verified }"
      >
        <router-link
          :to="{ name: 'StudentList', params: { id: classItem.id } }"
          class="class-link"
        >
          <div class="class-header d-flex align-center">
            <v-icon
              v-if="classItem.is_verified"
              color="success"
              size="small"
              class="mr-1"
            >
              mdi-check-circle
            </v-icon>
            <span class="class-name">{{ classItem.name }}</span>
          </div>
          <div class="class-meta">
            <span v-if="classItem.student_count" class="student-count">
              <v-icon size="small" class="mr-1">mdi-account-multiple</v-icon>
              {{ classItem.student_count }}
            </span>
          </div>
        </router-link>
      </div>
    </div>
  </v-container>
</template>

<style scoped>
.class-dashboard {
  max-width: 1200px;
  padding: 24px 16px;
  margin: auto;
}

form input {
  width: 100%;
  padding: 12px 50px 12px 16px;
  border-radius: 10px;
  border: 1px solid #ccc;
  font-size: 1rem;
  transition: 0.3s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

form span {
  padding: 12px 16px;
}

form input:focus {
  outline: none;
  border-color: #42a5f5;
  box-shadow: 0 0 0 3px rgba(66, 165, 245, 0.2);
}

.src-btn {
  cursor: pointer;
  right: 10px;
  color: #555;
  transition: 0.2s;
}

.src-btn:hover {
  color: #1976d2;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  text-align: center;
  color: #757575;
}

.class-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 32px;
}

.class-item {
  border-radius: 12px;
  /* background: #f5faff; */
  padding: 16px;
  border-left: 5px solid #fff2eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

/* .v-theme--dark .class-item {
  background: linear-gradient(90deg, #1d003f, #bbdefb);
} */

.class-item.verified {
  background: #e0f7fa;
  border-left-color: #00bcd4;
}

.class-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.class-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.class-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  gap: 6px;
}

.class-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.class-meta {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #607d8b;
  gap: 4px;
}

.student-count {
  display: flex;
  align-items: center;
}

@media (max-width: 600px) {
  .class-list {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }

  .class-header {
    font-size: 1rem;
  }

  .class-item {
    padding: 14px;
  }
}
</style>
